class BodyResponseTimeline extends HTMLElement {
  #controller = null;
  #mediaQuery = null;
  #resizeObserver = null;
  #activeIndex = 0;
  #rafId = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#mediaQuery = window.matchMedia('(min-width: 950px)');

    this.#buildMarkers();
    this.#observeLayout(signal);
    this.#bindScroll(signal);
    this.#mediaQuery.addEventListener('change', () => this.#handleModeChange(), { signal });

    this.#setActiveIndex(0);
    this.#handleModeChange();
    this.#updateProgress();
  }

  disconnectedCallback() {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }

    this.#resizeObserver?.disconnect();
    this.#controller?.abort();
    this.#controller = null;
    this.#mediaQuery = null;
  }

  #getCards() {
    return [...this.querySelectorAll('[data-body-timeline-card]')];
  }

  #getTrack() {
    return this.querySelector('[data-body-timeline-track]');
  }

  #getFill() {
    return this.querySelector('[data-body-timeline-fill]');
  }

  #getMarkersContainer() {
    return this.querySelector('[data-body-timeline-markers]');
  }

  #getCardsSpacer() {
    return this.querySelector('.body-response-timeline__cards-spacer');
  }

  #isMobileCardStackEnabled() {
    return this.dataset.mobileCardStack !== 'false';
  }

  #isMobileStaticCards() {
    return !this.#mediaQuery?.matches && !this.#isMobileCardStackEnabled();
  }

  #shouldPositionAnchoredMarkers() {
    return Boolean(this.#mediaQuery?.matches) || this.#isMobileStaticCards();
  }

  #clearStickyCardLayout() {
    const cards = this.#getCards();

    for (const card of cards) {
      card.style.removeProperty('--body-timeline-card-peek-offset');
      card.style.removeProperty('padding-bottom');
      card.style.removeProperty('margin-bottom');
    }

    this.#getCardsSpacer()?.style.removeProperty('height');
    this.style.removeProperty('--body-timeline-scale-height');
  }

  #getCardAnchor(card) {
    return card.querySelector('.body-timeline-card__period') ?? card.querySelector('.body-timeline-card__header') ?? card;
  }

  #getFocusY() {
    // Only used by the desktop alternating-card layout.
    return window.innerHeight * 0.36;
  }

  #observeLayout(signal) {
    const track = this.#getTrack();

    if (!track || typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#positionStickyCards();
      this.#positionMarkers();
      this.#updateProgress();
    });

    this.#resizeObserver.observe(track);
    window.addEventListener(
      'load',
      () => {
        this.#positionStickyCards();
        this.#positionMarkers();
      },
      { once: true, signal }
    );
  }

  #bindScroll(signal) {
    const onScroll = () => {
      if (this.#rafId !== null) return;

      this.#rafId = requestAnimationFrame(() => {
        this.#rafId = null;
        this.#updateProgress();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
  }

  #buildMarkers() {
    const markers = this.#getMarkersContainer();
    const cards = this.#getCards();

    if (!markers) return;

    markers.replaceChildren();

    this.style.setProperty('--body-timeline-card-count', String(cards.length));

    for (const [index, card] of cards.entries()) {
      const marker = document.createElement('span');
      marker.className = 'body-response-timeline__marker';
      marker.dataset.bodyTimelineMarker = '';
      markers.appendChild(marker);
      card.dataset.timelineIndex = String(index);
      card.style.setProperty('--body-timeline-card-index', String(index));
    }
  }

  #getHeaderPeekHeight(card, header) {
    if (!header) return 0;

    // Visible header zone from the card's top edge (card padding + header).
    return header.offsetTop + header.offsetHeight;
  }

  #getHeaderStackIncrement(header, stackGap) {
    if (!header) return stackGap;

    // Sticky step = header row height + the designed gap between stacked headers.
    // Card padding is excluded so the next card does not land an extra 12px lower.
    return header.offsetHeight + stackGap;
  }

  // Mobile cards stack with `position: sticky`, each one docking lower so only
  // the previous header row peeks out. The step is each header's measured
  // height plus `--body-timeline-card-header-stack-gap` (12px). Card padding
  // is not included in the step so headers sit 12px apart, not 24px.
  #positionStickyCards() {
    if (this.#mediaQuery?.matches) return;

    if (this.#isMobileStaticCards()) {
      this.#clearStickyCardLayout();
      this.#positionMarkers();
      return;
    }

    const cards = this.#getCards();
    if (cards.length === 0) return;

    const spacer = this.#getCardsSpacer();

    // Clear any previously-applied equalization spacing before re-measuring.
    for (const card of cards) {
      card.style.removeProperty('padding-bottom');
      card.style.removeProperty('margin-bottom');
    }

    spacer?.style.removeProperty('height');

    const clearance =
      Number.parseFloat(getComputedStyle(cards[0]).getPropertyValue('--body-timeline-card-sticky-clearance')) || 0;
    const stackGap =
      Number.parseFloat(getComputedStyle(cards[0]).getPropertyValue('--body-timeline-card-header-stack-gap')) || 12;

    let cumulativeOffset = 0;
    const entries = [];

    for (const [index, card] of cards.entries()) {
      card.style.setProperty('--body-timeline-card-peek-offset', `${cumulativeOffset}px`);

      const header = card.querySelector('.body-timeline-card__header');
      const headerPeekHeight = this.#getHeaderPeekHeight(card, header);
      const headerCenter =
        headerPeekHeight > 0 && header
          ? header.offsetTop + header.offsetHeight / 2
          : headerPeekHeight / 2;

      entries.push({
        card,
        peekOffset: cumulativeOffset,
        headerPeekHeight,
        headerCenter,
        top: clearance + cumulativeOffset,
        height: card.offsetHeight,
      });

      if (index < cards.length - 1) {
        cumulativeOffset += this.#getHeaderStackIncrement(header, stackGap);
      }
    }

    const maxBottom = Math.max(...entries.map((entry) => entry.top + entry.height));
    const totalDeficit = entries.reduce((sum, entry) => {
      const deficit = maxBottom - entry.top - entry.height;
      return deficit > 0.5 ? sum + deficit : sum;
    }, 0);

    if (spacer) {
      spacer.style.height = totalDeficit > 0.5 ? `${totalDeficit}px` : '0';
    }

    // Give the sticky scale the same clearance as the cards, but cap its
    // height to the fully-cascaded stack height (instead of the tall,
    // un-stacked flex column height) so it also has real room to hold its
    // stuck position — same reasoning as the cards-spacer element.
    this.style.setProperty('--body-timeline-scale-height', `${maxBottom - clearance}px`);

    // A marker's position within the (now sticky) scale is just the card's
    // peek offset plus half its header height, since the scale and cards
    // share the same clearance/top origin.
    const markers = [...this.querySelectorAll('[data-body-timeline-marker]')];

    for (const [index, marker] of markers.entries()) {
      const entry = entries[index];
      if (!entry) continue;

      marker.style.top = `${entry.peekOffset + entry.headerCenter}px`;
    }
  }

  // Desktop markers track the alternating cards' absolute (non-sticky)
  // positions, which only change on layout/resize, not on scroll.
  #positionMarkers() {
    if (!this.#shouldPositionAnchoredMarkers()) return;

    const track = this.#getTrack();
    const markers = [...this.querySelectorAll('[data-body-timeline-marker]')];
    const cards = this.#getCards();

    if (!track || markers.length === 0 || markers.length !== cards.length) return;

    const trackRect = track.getBoundingClientRect();

    for (const [index, marker] of markers.entries()) {
      const card = cards[index];
      const anchor = this.#getCardAnchor(card);
      const anchorRect = anchor.getBoundingClientRect();
      const centerY = anchorRect.top + anchorRect.height / 2 - trackRect.top;

      marker.style.top = `${centerY}px`;
    }
  }

  #updateActiveFromScroll() {
    const cards = this.#getCards();

    if (cards.length === 0) return;

    let nextIndex = 0;

    if (this.#mediaQuery?.matches || this.#isMobileStaticCards()) {
      // Desktop and mobile static layout: whichever card's anchor has crossed
      // the focus line is the active one.
      const focusY = this.#getFocusY();

      for (const [index, card] of cards.entries()) {
        const anchor = this.#getCardAnchor(card);
        const anchorRect = anchor.getBoundingClientRect();
        const centerY = anchorRect.top + anchorRect.height / 2;

        if (centerY <= focusY) {
          nextIndex = index;
        }
      }
    } else {
      // Mobile: a card is active once it has docked at its sticky offset
      // (i.e. it's now the topmost, fully visible card). It stays active
      // until the next card also reaches its own offset and covers it.
      for (const [index, card] of cards.entries()) {
        const stickyTop = Number.parseFloat(getComputedStyle(card).top) || 0;

        if (card.getBoundingClientRect().top <= stickyTop + 1) {
          nextIndex = index;
        }
      }
    }

    if (nextIndex !== this.#activeIndex) {
      this.#setActiveIndex(nextIndex);
    }
  }

  #setActiveIndex(index) {
    const cards = this.#getCards();
    const markers = [...this.querySelectorAll('[data-body-timeline-marker]')];

    this.#activeIndex = index;

    for (const [cardIndex, card] of cards.entries()) {
      card.classList.toggle('body-timeline-card--active', cardIndex === index);
    }

    for (const [markerIndex, marker] of markers.entries()) {
      marker.classList.toggle('body-response-timeline__marker--active', markerIndex <= index);
      marker.classList.toggle('body-response-timeline__marker--current', markerIndex === index);
    }
  }

  #updateProgress() {
    const track = this.#getTrack();
    const fill = this.#getFill();

    if (!track || !fill) return;

    const rect = track.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const trackHeight = rect.height;

    if (trackHeight <= 0) return;

    const start = rect.top - viewportHeight * 0.35;
    const end = rect.bottom - viewportHeight * 0.65;
    const distance = end - start;
    const rawProgress = distance <= 0 ? 1 : (0 - start) / distance;
    const progress = Math.min(1, Math.max(0, rawProgress));

    fill.style.setProperty('--body-timeline-progress', String(progress));
    fill.style.height = `${progress * 100}%`;

    this.#updateActiveFromScroll();
  }

  #handleModeChange() {
    const cards = this.#getCards();

    if (cards.length === 0) return;

    if (this.#mediaQuery?.matches || this.#isMobileStaticCards()) {
      this.#clearStickyCardLayout();
    } else {
      this.#positionStickyCards();
    }

    this.#positionMarkers();
    this.#updateActiveFromScroll();
    this.#updateProgress();
  }
}

if (!customElements.get('body-response-timeline')) {
  customElements.define('body-response-timeline', BodyResponseTimeline);
}
